import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAppContext } from 'src/components/app-context';

import { IconClose } from '@prisjakt/ui/icon/close';
import { PanelTitle } from '@prisjakt/ui/panel';
import { Button, ButtonSize, ButtonType } from '@prisjakt/ui/button';
import { OutlinedButton } from '@prisjakt/ui/button/variants';

import { DesktopOnly, MediumDown } from 'src/ui/block';
import { Aggregations } from '../aggregations';
import Search from '../search';
import Popular from '../popular';
import Categories from '../categories';
import Stores, { mapStores } from '../stores';
import TagCloud from '../tag-cloud';
import FilterRanges from './ranges';

import type { AppState } from 'src/reducers';
import {
  campaignPopularQuery,
  campaignCategoryFilter,
  campaignsRangeFilter,
} from '../../actions';

import {
  trackFilterSelectEvent,
  FilterScope,
} from 'src/globals/tracking/filter';

import { RANGE_OPTIONS, DEFAULT_VISIBLE_STORES } from '../../constants';

import type { MarketConfig } from '@prisjakt/web-app-config/markets/types';
import type * as H from 'history';
import type { CampaignPageQuery } from 'src/types';
import type {
  CampaignFilterStore,
  RouterParams,
  SelectedFilter,
  UrlParams,
} from '../../types';

import {
  StyledFilter,
  StyledFilterContent,
  StyledFiltersButton,
  StyledFiltersHeader,
  StyledFilterRanges,
  StyledFilters,
  StyledFiltersPopular,
  StyledFilterShowButton,
  StyledFiltersWrapper,
  StyledSearch,
  StyledTags,
} from './styles';

type Props = {
  buildRouter: (location: Omit<H.Location, 'state' | 'hash'>) => void;
  campaign: CampaignPageQuery['campaign'];
  categoryTree: CampaignPageQuery['categoryTree'];
  clearFilter: (keepSort: boolean) => void;
  executeSearch: (search: string) => void;
  router: RouterParams;
  selectedFilters: SelectedFilter[];
  setShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
  setStores: React.Dispatch<React.SetStateAction<CampaignFilterStore[]>>;
  showFilter: boolean;
  updateUrlParams: (item: UrlParams) => void;
};

export const CampaignFilters = (props: Props) => {
  const {
    buildRouter,
    campaign,
    categoryTree,
    clearFilter,
    executeSearch,
    router,
    selectedFilters,
    setShowFilter,
    setStores,
    showFilter,
    updateUrlParams,
  } = props;

  const { t } = useAppContext();

  const [expanded, setExpanded] = useState<string[]>([]);

  const history = useHistory();
  const dispatch = useDispatch();

  const popularCampaignFilterPaths: MarketConfig['popularCampaignFilterPaths'] =
    useSelector(
      (state: AppState) => state.areas.campaign.popularCampaignFilterPaths
    );

  const filterLayout = 'campaign-filter';
  const selectionsCount = selectedFilters.length;

  if (!campaign?.id) return null;

  return (
    <>
      <MediumDown>
        <StyledTags>
          <StyledFilterShowButton data-test="FilterShowButton">
            <OutlinedButton
              onClick={() => setShowFilter(true)}
              size={ButtonSize.SMALL}
            >
              {t('campaigns', 'filter.show-button')}
            </OutlinedButton>
          </StyledFilterShowButton>
          {selectionsCount > 0 && (
            <TagCloud
              selectionsCount={selectionsCount}
              selected={selectedFilters}
              onChange={item =>
                updateUrlParams({
                  key: item.id,
                  value: item.value,
                  isGrouped: item.isGrouped,
                })
              }
              onClear={() => clearFilter(true)}
            />
          )}
        </StyledTags>
      </MediumDown>

      <StyledFiltersWrapper $show={showFilter}>
        <StyledFilters $show={showFilter}>
          <StyledFilter>
            <StyledFilterContent>
              <PanelTitle>{t('campaigns', 'filter.title')}</PanelTitle>

              <DesktopOnly>
                <>
                  <StyledSearch data-test="CampaignFilterSearch">
                    <Search
                      query={router.query}
                      onSearch={item => {
                        trackFilterSelectEvent({
                          value: item.value,
                          scope: FilterScope.CAMPAIGN,
                          property: 'campaign-name-search',
                          layout: filterLayout,
                          type: 'campaign-name-search',
                          campaign: campaign.name,
                        });
                        updateUrlParams({
                          key: item.id,
                          value: item.value,
                        });
                      }}
                    />
                  </StyledSearch>

                  <StyledTags data-test="CampaignSelectedFilterTags">
                    {selectionsCount > 0 && (
                      <TagCloud
                        selectionsCount={selectionsCount}
                        selected={selectedFilters}
                        onChange={item => {
                          updateUrlParams({
                            key: item.id,
                            value: item.value,
                            isGrouped: item.isGrouped,
                          });
                        }}
                        onClear={() => clearFilter(true)}
                      />
                    )}
                  </StyledTags>
                </>
              </DesktopOnly>

              <StyledFiltersPopular data-test="CampaignFiltersPopular">
                <h6>{t('campaigns', 'popular-filters.title')}</h6>
                {popularCampaignFilterPaths ? (
                  <Popular
                    filterPaths={popularCampaignFilterPaths}
                    location={history.location}
                    onChange={item => {
                      trackFilterSelectEvent({
                        value: item.title,
                        scope: FilterScope.CAMPAIGN,
                        property: 'popular-deals-link',
                        layout: filterLayout,
                        type: 'popular-deals',
                        campaign: campaign.name,
                      });
                      const routerParams = buildRouter(item);

                      dispatch(
                        campaignPopularQuery(
                          routerParams,
                          popularCampaignFilterPaths?.find(
                            filter => filter.path === item.search
                          )
                        )
                      );

                      setShowFilter(false);
                      executeSearch(item.search);
                    }}
                  />
                ) : null}
              </StyledFiltersPopular>
              <Aggregations
                campaignId={campaign.id}
                router={router}
                categoryTree={categoryTree}
                expandedCategories={expanded}
                onSuccess={data => {
                  setStores(mapStores(data.aggregations));
                }}
                render={data => {
                  const storeItems = data && mapStores(data.aggregations);
                  if (!campaign.id) return null;

                  return (
                    <div data-test="CampaignCategories">
                      <h6>{t('campaigns', 'category.title')}</h6>
                      <Categories
                        campaignId={campaign.id}
                        aggregations={
                          data?.aggregations?.category?.current.buckets
                        }
                        onChange={(item, category) => {
                          trackFilterSelectEvent({
                            value: category.id,
                            scope: FilterScope.CAMPAIGN,
                            property: 'category',
                            layout: filterLayout,
                            type: 'list',
                            campaign: campaign.name,
                          });
                          updateUrlParams({
                            key: 'category',
                            value: item.value,
                            isGrouped: true,
                          });
                          dispatch(campaignCategoryFilter(category));
                        }}
                        categoryTree={categoryTree}
                        selected={router.categories}
                        expanded={expanded}
                        onExpand={(e, id) => {
                          e.preventDefault();
                          setExpanded(prevState =>
                            prevState.includes(id)
                              ? prevState.filter(item => item !== id)
                              : prevState.concat([id])
                          );
                        }}
                      />
                      <StyledFilterRanges>
                        <FilterRanges
                          campaignId={campaign.id}
                          data={data}
                          router={router}
                          rangeOptions={RANGE_OPTIONS}
                          onChange={item => {
                            trackFilterSelectEvent({
                              value: `${item.keep.from} - ${item.keep.to}`,
                              scope: FilterScope.CAMPAIGN,
                              property: item.id,
                              layout: filterLayout,
                              type: 'range',
                              campaign: campaign.name,
                            });
                            updateUrlParams({
                              key: item.id,
                              value: item.value,
                              keep: item.keep,
                            });
                            dispatch(campaignsRangeFilter(item));
                          }}
                        />
                      </StyledFilterRanges>
                      <h6>{t('campaigns', 'store.title')}</h6>
                      <Stores
                        campaignId={campaign.id}
                        stores={storeItems}
                        hasMore={Boolean(
                          data &&
                            data.aggregations?.stores?.current
                              ?.sum_other_doc_count &&
                            data.aggregations.stores.current
                              ?.sum_other_doc_count > 0
                        )}
                        showingMore={Boolean(
                          storeItems &&
                            storeItems.length > DEFAULT_VISIBLE_STORES
                        )}
                        onChange={item => {
                          trackFilterSelectEvent({
                            value: item.value,
                            scope: FilterScope.CAMPAIGN,
                            property: item.id,
                            layout: filterLayout,
                            type: 'list',
                            campaign: campaign.name,
                          });
                          updateUrlParams({
                            key: 'stores',
                            value: item.value,
                            isGrouped: true,
                          });
                        }}
                        onMoreChanged={showMore => {
                          if (showMore) {
                            updateUrlParams({
                              key: 'max_stores',
                              value: 100,
                            });
                          } else {
                            updateUrlParams({
                              key: 'max_stores',
                              value: DEFAULT_VISIBLE_STORES,
                            });
                          }
                        }}
                        selected={router.stores}
                      />
                    </div>
                  );
                }}
              />
            </StyledFilterContent>
          </StyledFilter>

          <StyledFiltersHeader>
            <h4>
              {t('campaigns', 'filter.title')}
              <button type="button" onClick={() => setShowFilter(false)}>
                <IconClose />
              </button>
            </h4>
            <StyledFiltersButton data-test="FiltersButton">
              <Button
                type={
                  selectionsCount === 0
                    ? ButtonType.OUTLINED
                    : ButtonType.PRIMARY
                }
                onClick={() => setShowFilter(false)}
              >
                {t('campaigns', 'filter.apply-button')}
              </Button>
            </StyledFiltersButton>
          </StyledFiltersHeader>
        </StyledFilters>
      </StyledFiltersWrapper>
    </>
  );
};
